import {
  Dropdown,
  DropdownOption,
  FaroSlider,
  FaroSwitch,
  FaroText,
} from "@faro-lotv/flat-ui";
import { assert, GapFillOptimizedFor } from "@faro-lotv/lotv";
import Stack from "@mui/material/Stack";
import { type JSX } from "react";
import { DebugOverlaySection } from "./debug-overlay-section";
import { usePostProcessingSettings } from "./post-processing-settings";

/** @returns UI for configuring post-processing effects. */
export function PostProcessingView(): JSX.Element | null {
  return (
    <DebugOverlaySection title="Post-processing">
      <PostProcessing />
    </DebugOverlaySection>
  );
}

function PostProcessing(): JSX.Element {
  const {
    isEnabled,
    setIsEnabled,
    gapFillSettings,
    setGapFilling,
    ambientOcclusionSettings,
    setAmbientOcclusion,
    eyeDomeStrength,
    eyeDomeOutline,
    setEyeDome,
    antiAliasingSettings,
    setAntiAliasing,
  } = usePostProcessingSettings();

  let gapFillValue;
  switch (gapFillSettings) {
    case GapFillOptimizedFor.Speed:
      gapFillValue = "Speed";
      break;
    case GapFillOptimizedFor.GapSize:
      gapFillValue = "Quality";
      break;
    case undefined:
      gapFillValue = "Off";
      break;
  }

  return (
    <Stack>
      <FaroSwitch
        dark
        fullWidth
        checked={isEnabled}
        onToggle={() => setIsEnabled(!isEnabled)}
        label="Enabled"
      />

      <FaroSwitch
        dark
        fullWidth
        disabled={!isEnabled}
        checked={ambientOcclusionSettings}
        onToggle={() => setAmbientOcclusion(!ambientOcclusionSettings)}
        label="Ambient Occlusion"
      />

      <FaroText variant="bodyM" dark>
        Eye Dome Strength (0 = disabled)
      </FaroText>
      <FaroSlider
        dark
        value={eyeDomeStrength}
        min={0.0}
        max={1.0}
        step={0.01}
        valueLabelDisplay="auto"
        size="small"
        onChange={(_event, value) => {
          assert(
            typeof value === "number",
            "Eye dome strength should be a number",
          );

          setEyeDome(value, eyeDomeOutline);
        }}
      />

      <FaroSwitch
        dark
        fullWidth
        disabled={!isEnabled || eyeDomeStrength === 0}
        checked={eyeDomeOutline}
        onToggle={() => setEyeDome(eyeDomeStrength, !eyeDomeOutline)}
        label="Eye Dome Outline"
      />

      <Dropdown
        dark
        disabled={!isEnabled}
        value={gapFillValue}
        onChange={(event) => {
          const { value } = event.target;
          switch (value) {
            case "Speed":
              setGapFilling(GapFillOptimizedFor.Speed);
              break;
            case "Quality":
              setGapFilling(GapFillOptimizedFor.GapSize);
              break;
            default:
              setGapFilling(undefined);
              break;
          }
        }}
        options={GAP_FILLING_OPTIONS}
        label="Gap Filling"
      />

      <FaroSwitch
        dark
        fullWidth
        disabled={!isEnabled}
        checked={antiAliasingSettings}
        onToggle={() => setAntiAliasing(!antiAliasingSettings)}
        label="Anti-aliasing"
      />
    </Stack>
  );
}

const GAP_FILLING_OPTIONS: DropdownOption[] = [
  { key: "Off", value: "Off", label: "Off" },
  {
    key: "Speed",
    value: "Speed",
    label: "Speed",
  },
  {
    key: "Quality",
    value: "Quality",
    label: "Quality",
  },
];
